'use client';

import { useAppInsights } from '../AppInsightsWrapper/AppInsightsWrapper';

import { faBug } from '@fortawesome/free-solid-svg-icons';
import { ActionIcon, Box, Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { type ReactNode, useCallback, useEffect } from 'react';

import { logException, logWarning } from '../../common/util/logging/writeLog';
import { ContentWithDefault } from '../contentWithDefault/ContentWithDefault';
import { Icon } from '../Spinner/Icon';
import styles from './CommonError.module.scss';

export interface ErrorProps {
	tooltipContent?: ReactNode;
	error: Error & { digest?: string };
	reset?: () => Promise<unknown>;
}

function DefaultErrorContent(): ReactNode {
	return <>Something went wrong</>;
}

export function CommonError(props: ErrorProps): ReactNode {
	const [opened, { close, open }] = useDisclosure(false);
	const appInsights = useAppInsights();

	const error = props.error;
	const reset = props.reset;

	useEffect(() => {
		if (error) {
			// Log the error to an error reporting service
			logException(appInsights, error);
		}
	}, [error, appInsights]);

	const attemptReset = useCallback(() => {
		if (!reset) {
			return;
		}

		// Attempt to recover by trying to re-render the segment
		logWarning(appInsights, 'Attempting to reload component');
		void reset();
	}, [appInsights, reset]);

	return (
		<Box className={styles['main']}>
			<Popover
				opened={opened}
				position="bottom"
				withArrow={true}
				classNames={{ arrow: styles['errorTooltipArrow'] }}
			>
				<Popover.Target>
					<ActionIcon
						onClick={attemptReset}
						onMouseEnter={open}
						onMouseLeave={close}
						onFocus={open}
						onBlur={close}
						color="#990000"
					>
						<Icon className="errorIcon" icon={faBug} aria-label="Error" />
					</ActionIcon>
				</Popover.Target>
				<Popover.Dropdown className={styles['errorTooltip']}>
					<div className={styles['errorTooltipHeader']}>Error</div>
					<div className={styles['errorTooltipBody']}>
						<ContentWithDefault content={props.tooltipContent} defaultContent={<DefaultErrorContent />} />
					</div>
				</Popover.Dropdown>
			</Popover>
		</Box>
	);
}
